<script setup lang="ts">
import {
  differenceInWeeks,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
} from "date-fns";
import { getCurrencyRestrictions } from "~/currenciesData/getCurrencyRestrictions";

interface Props {
  title: string;
  description: string;
  imageUrl: string;
  advertiserName: string;
  endsAt: Date;
  budget: string | null;
  currency: string;
  usdConversionRate: string | null;
}

const props = defineProps<Props>();

const readableEndsAt = computed(() => {
  const now = new Date();
  const diffInWeeks = differenceInWeeks(props.endsAt, now);
  const diffInDays = differenceInDays(props.endsAt, now) % 7;
  const diffInHours = differenceInHours(props.endsAt, now) % 24;
  const diffInMinutes = differenceInMinutes(props.endsAt, now) % 60;

  if (diffInWeeks > 0) {
    return `ends in ${diffInWeeks} week${diffInWeeks > 1 ? "s" : ""}`;
  }
  if (diffInDays > 0) {
    return `ends in ${diffInDays} day${diffInDays > 1 ? "s" : ""}`;
  }
  if (diffInHours > 0) {
    return `ends in ${diffInHours} hour${diffInHours > 1 ? "s" : ""}`;
  }
  if (diffInMinutes > 0) {
    return `ends in ${diffInMinutes} minute${diffInMinutes > 1 ? "s" : ""}`;
  }
  return "ended";
});

const currencyDescription = computed(() => {
  return getCurrencyRestrictions(props.currency).currencyDescription;
});
</script>
<template>
  <div
    class="hover:translate-y-[-3px] transition-all duration-300 ease-out cursor-pointer bg-white grid md:grid-cols-[1fr_350px] p-6 rounded-2xl"
  >
    <div class="text-black">
      <p class="text-lg">{{ title }}</p>
      <p
        class="text-sm mt-4 max-w-[720px] line-clamp-2"
        v-html="description"
      ></p>

      <div class="flex text-black/50 mt-2 md:mt-4 items-center gap-2">
        <img :src="imageUrl" alt="Campaign Image" class="w-6 h-6" />
        <p class="text-sm">{{ advertiserName }}</p>
        <p>•</p>
        <p class="text-sm">{{ readableEndsAt }}</p>
      </div>
    </div>

    <div class="text-black mt-8 md:mt-0">
      <p class="text-sm">Total budget</p>

      <div class="mt-2">
        <p class="text-[46px]">
          {{ budget ? Number(budget).toLocaleString("en-US") : "" }}
          <span class="text-2xl"> {{ currency }}</span>
        </p>
        <p
          v-if="usdConversionRate && !currencyDescription"
          class="text-sm text-[#707070]"
        >
          <span> Approx. ~$</span>
          {{ Number(usdConversionRate) * Number(budget) }}
        </p>

        <p v-if="currencyDescription">
          <span class="text-sm text-[#707070]">
            {{ currencyDescription }}
          </span>
        </p>
      </div>
    </div>
  </div>
</template>
